'use client';

import { useEffect, useRef } from 'react';
import { API_ORIGIN, resolveApiPath } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { shouldPollCharacterElementTask } from '../_utils';
import type { CharacterElementTaskStateReply } from '../types';

const ELEMENT_TASK_STATE_SSE_PATH = resolveApiPath('/vidu/v1/material/elements/state');
const ELEMENT_TASK_STATE_RECONNECT_INTERVAL = 3000;

interface UseCharacterElementTaskSseOptions {
  taskId?: string;
  enabled: boolean;
  onState: (state: CharacterElementTaskStateReply) => void;
}

export function useCharacterElementTaskSse({ taskId, enabled, onState }: UseCharacterElementTaskSseOptions): void {
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const shouldKeepSSERef = useRef(false);
  const taskIdRef = useRef(taskId);
  taskIdRef.current = taskId;
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const { run: getTaskState, close: closeGetTaskState } = useFetchStream<{ task_id: string }>(
    ELEMENT_TASK_STATE_SSE_PATH,
    {
      baseURL: API_ORIGIN,
      onMessage: (str) => {
        const dataObjects = str
          .split('data:')
          .map((data) => data.trim())
          .filter(Boolean);

        for (const dataString of dataObjects) {
          try {
            const data = JSON.parse(dataString) as CharacterElementTaskStateReply;
            onStateRef.current(data);
            shouldKeepSSERef.current = shouldPollCharacterElementTask(data.state);
          } catch (error) {
            console.error('Error_from_character_element_task_state: ', error);
          }
        }
      },
      onClose: () => {
        clearReconnectTimer();
        if (shouldKeepSSERef.current && taskIdRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            if (shouldKeepSSERef.current && taskIdRef.current) {
              getTaskState({ task_id: taskIdRef.current });
            }
          }, ELEMENT_TASK_STATE_RECONNECT_INTERVAL);
        }
      },
    },
  );

  useEffect(() => {
    if (!enabled || !taskId) {
      shouldKeepSSERef.current = false;
      clearReconnectTimer();
      closeGetTaskState();
      return;
    }

    shouldKeepSSERef.current = true;
    clearReconnectTimer();
    getTaskState({ task_id: taskId });

    return () => {
      shouldKeepSSERef.current = false;
      clearReconnectTimer();
      closeGetTaskState();
    };
  }, [enabled, taskId, getTaskState, closeGetTaskState]);
}
